import React from "react";
import { useNavigate, useParams } from "react-router";
import { I } from "../redesign/kvpIcons";
import { Toast } from "../redesign/Toast";
import { MeldeDialog } from "./MeldeDialog";
import { TicketDetail } from "./TicketDetail";
import { ListenParams, ticketService } from "./ticketService";
import {
  ART_LABEL, BEREICHE_AUSWAHL, BEREICH_LABEL, PRIO_LABEL, REITER, REITER_LABEL, STATUS_LABEL, TicketAnzahl, TicketListenZeile, TicketPrio,
  reiterAnzahl,
} from "./ticketTypen";
import { fehlerText } from "./ticketFehler";
import "./tickets.css";

const datum = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });

const FILTER_LEER: Pick<ListenParams, "art" | "bereich" | "prio" | "nurMeine" | "tenant" | "q"> = {
  art: undefined, bereich: undefined, prio: undefined, nurMeine: undefined, tenant: undefined, q: undefined,
};

export default function TicketsPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const gewaehlt = id ? Number(id) : null;
  const [filter, setFilter] = React.useState<ListenParams>({ status: "offen", sort: "aktivitaet" });
  const [daten, setDaten] = React.useState<{ tickets: TicketListenZeile[]; anzahl: TicketAnzahl } | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [neu, setNeu] = React.useState(false);
  const [gemeldet, setGemeldet] = React.useState<number | null>(null);
  // Alle je geladenen Mandanten-Ids (Union über Ladevorgänge) — NICHT nur die
  // der aktuell gefilterten Liste. Sonst verschwindet der Mandant-Filter samt
  // „Alle Mandanten" genau dann, wenn er greift (die gefilterte Liste enthält
  // dann nur noch einen Mandanten) — eine Sackgasse ohne Weg zurück.
  const [mandantenGesehen, setMandantenGesehen] = React.useState<Set<string>>(new Set());

  const laden = React.useCallback(async () => {
    try {
      const d = await ticketService.liste(filter);
      setDaten(d);
      setFehler(null);
      setMandantenGesehen((alt) => {
        const neu = new Set(alt);
        let geaendert = false;
        for (const t of d.tickets) if (!neu.has(t.tenantId)) { neu.add(t.tenantId); geaendert = true; }
        return geaendert ? neu : alt;
      });
    } catch (e) { setFehler(fehlerText(e, "Tickets konnten nicht geladen werden.")); }
  }, [filter]);
  // Im Detail wird die Liste nicht gebraucht; beim Zurückkehren frisch laden.
  React.useEffect(() => {
    if (gewaehlt) return;
    const h = setTimeout(() => void laden(), filter.q ? 250 : 0);
    return () => clearTimeout(h);
  }, [laden, filter.q, gewaehlt]);

  const setze = (p: Partial<ListenParams>) => setFilter((f) => ({ ...f, ...p }));
  const filterZuruecksetzen = () => setFilter((f) => ({ ...f, ...FILTER_LEER }));
  const mandanten = Array.from(mandantenGesehen);
  const filterAktiv = !!(filter.art || filter.bereich || filter.prio || filter.nurMeine || filter.tenant || filter.q);

  const neuDialog = neu && (
    <MeldeDialog kontext={null} onSchliessen={() => setNeu(false)}
      onGesendet={(nid) => { setNeu(false); setGemeldet(nid); navigate(`/einstellungen/tickets/${nid}`); }} />
  );
  const toast = <Toast open={gemeldet != null} tone="success" message={`T-${gemeldet} angelegt`} onClose={() => setGemeldet(null)} />;

  // Eigene Detailansicht in voller Breite statt Split — die Liste ist dann ausgeblendet.
  if (gewaehlt) {
    return (
      <div className="kvp-rd tp-page tk-seite">
        <TicketDetail key={gewaehlt} id={gewaehlt} onGeaendert={() => {}} onZurueck={() => navigate("/einstellungen/tickets")} />
        {toast}
      </div>
    );
  }

  return (
    <div className="kvp-rd tp-page tk-seite">
      <header className="tp-header">
        <h1>Tickets</h1>
        <button type="button" className="cd-save" onClick={() => setNeu(true)}>{I.plus} Neues Ticket</button>
      </header>

      <div className="tp-tabs">
        {REITER.map((r) => (
          <button key={r} type="button" className={`tp-tab${filter.status === r ? " on" : ""}`} onClick={() => setze({ status: r })}>
            {REITER_LABEL[r]}
            <span className="tp-tab-badge">{reiterAnzahl(daten?.anzahl, r)}</span>
          </button>
        ))}
      </div>

      <div className="tp-toolbar tk-filter">
        <label className="search">{I.search}<input placeholder="Nr., Betreff, Inhalt" value={filter.q ?? ""} onChange={(e) => setze({ q: e.target.value })} /></label>
        <select className="tk-select" aria-label="Art" value={filter.art ?? ""} onChange={(e) => setze({ art: e.target.value || undefined })}>
          <option value="">Alle Arten</option>
          {Object.entries(ART_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="tk-select" aria-label="Bereich" value={filter.bereich ?? ""} onChange={(e) => setze({ bereich: e.target.value || undefined })}>
          <option value="">Alle Bereiche</option>
          {BEREICHE_AUSWAHL.map((b) => <option key={b} value={b}>{BEREICH_LABEL[b]}</option>)}
        </select>
        <select className="tk-select" aria-label="Prio" value={filter.prio ?? ""} onChange={(e) => setze({ prio: e.target.value || undefined })}>
          <option value="">Alle Prios</option>
          {(Object.keys(PRIO_LABEL) as TicketPrio[]).map((p) => <option key={p} value={p}>{PRIO_LABEL[p]}</option>)}
        </select>
        {(mandanten.length > 1 || filter.tenant) && (
          <select className="tk-select" aria-label="Mandant" value={filter.tenant ?? ""} onChange={(e) => setze({ tenant: e.target.value || undefined })}>
            <option value="">Alle Mandanten</option>
            {mandanten.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        <button type="button" className={`tp-chip${filter.nurMeine ? " on" : ""}`} onClick={() => setze({ nurMeine: !filter.nurMeine })}>Nur meine</button>
        <select className="tk-select" aria-label="Sortierung" value={filter.sort} onChange={(e) => setze({ sort: e.target.value as ListenParams["sort"] })}>
          <option value="aktivitaet">Letzte Aktivität</option>
          <option value="prio">Prio zuerst</option>
        </select>
        {filterAktiv && (
          <button type="button" className="tk-filter-reset" onClick={filterZuruecksetzen}>Filter zurücksetzen</button>
        )}
      </div>

      <div className="tp-cols">
        <div className="tp-main">
          <div className="tp-listwrap">
            {fehler && <div className="tp-err">{fehler}</div>}
            {daten && daten.tickets.length === 0 && <div className="tp-empty">Keine Tickets in dieser Ansicht.</div>}
            {daten?.tickets.map((t) => (
              <button key={t.id} type="button" className="tp-row tk-zeile"
                onClick={() => navigate(`/einstellungen/tickets/${t.id}`)}>
                <span className="tk-nr">T-{t.id}</span>
                <span className="tk-art" title={ART_LABEL[t.art]}>{t.art === "idee" ? I.idee : I.feedback}</span>
                <span className="tk-betreff">{t.betreff}</span>
                <span className="tk-leise">{BEREICH_LABEL[t.bereich] ?? t.bereich}</span>
                <span className={`tk-punkt tk-prio--${t.prio ?? "keine"}`} title={t.prio ? PRIO_LABEL[t.prio] : "Prio ungesetzt"} />
                <span className={`tk-status tk-status--${t.status}`}>{STATUS_LABEL[t.status]}</span>
                <span className="tk-leise">{t.erstelltVonName}</span>
                <span className="tk-leise">{datum(t.createdAt)}</span>
                <span className="tk-leise">{datum(t.letzteAktivitaet)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {neuDialog}
      {toast}
    </div>
  );
}
